import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";

export type NavDir = "up" | "down" | "left" | "right";

type Handlers = {
  move: (dir: NavDir) => void;
  clearSearch: () => void;
};

type NavContextValue = {
  register: (handlers: Handlers) => () => void;
  move: (dir: NavDir) => void;
  clearSearch: () => void;
};

const PokedexNavContext = createContext<NavContextValue | null>(null);

export function PokedexNavProvider({ children }: { children: ReactNode }) {
  const handlersRef = useRef<Handlers | null>(null);

  const register = useCallback((handlers: Handlers) => {
    handlersRef.current = handlers;
    return () => {
      if (handlersRef.current === handlers) {
        handlersRef.current = null;
      }
    };
  }, []);

  const move = useCallback((dir: NavDir) => {
    handlersRef.current?.move(dir);
  }, []);

  const clearSearch = useCallback(() => {
    handlersRef.current?.clearSearch();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const inField =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement;

      if (event.key === "Escape") {
        clearSearch();
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        move("up");
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        move("down");
        return;
      }

      if (inField) {
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        move("left");
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        move("right");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [move, clearSearch]);

  return (
    <PokedexNavContext.Provider value={{ register, move, clearSearch }}>
      {children}
    </PokedexNavContext.Provider>
  );
}

export function usePokedexNav() {
  const ctx = useContext(PokedexNavContext);
  if (!ctx) {
    throw new Error("usePokedexNav must be used within PokedexNavProvider");
  }
  return ctx;
}
